import React, { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { COURSE_STATUS, COURSE_STATUS_OPTIONS } from "consts/courseType";
import { FormProvider, useForm } from "react-hook-form";
import { CourseBaseInfoDto, coursesApi } from "api";
import { PaziaButton } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { PaziaSelect } from "components/atoms";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { useToaster } from "utils";
import { PaziaBanner } from "components/atoms";
import { CourseStatus } from "../CourseStatus";

interface ChangeStatusModalProps {
  course: CourseBaseInfoDto;
  onCloseRequest: () => void;
  isOpen: boolean;
}

const schema = yup
  .object({
    newStatus: yup.number().required(),
  })
  .required();

export type FormValues = yup.InferType<typeof schema>;

export const ChangeStatusModal = ({ onCloseRequest, isOpen, course }: ChangeStatusModalProps) => {
  const { showError, showSuccess } = useToaster();
  const [confirmedChange, setConfirmedChange] = useState<number | null>(null);

  const onConfirmDraft = async () => {
    try {
      await coursesApi.changeStatusToDraft(course.id);
      showSuccess("Status kursu zaktualizowany");
    } catch (error) {
      showError("Nie udało się zmienić statusu kursu");
    } finally {
      onCloseRequest();
      setConfirmedChange(null);
    }
  };

  const onConfirmArchived = async () => {
    try {
      await coursesApi.changeStatusToArchived(course.id);
      showSuccess("Status kursu zaktualizowany");
    } catch (error) {
      showError("Nie udało się zmienić statusu kursu");
    } finally {
      onCloseRequest();
      setConfirmedChange(null);
    }
  };

  const onConfirmedReady = async () => {
    try {
      await coursesApi.changeStatusToReady(course.id);
      showSuccess("Status kursu zaktualizowany");
    } catch (error) {
      showError("Nie udało się zmienić statusu kursu");
    } finally {
      onCloseRequest();
      setConfirmedChange(null);
    }
  };

  const onConfirmedOngoing = async () => {
    try {
      await coursesApi.changeStatusToOngoing(course.id);
      showSuccess("Status kursu zaktualizowany");
    } catch (error) {
      showError("Nie udało się zmienić statusu kursu");
    } finally {
      onCloseRequest();
      setConfirmedChange(null);
    }
  };

  return (
    <PaziaModal
      id="change-status-modal"
      onCloseRequest={() => {
        onCloseRequest();
        setConfirmedChange(null);
      }}
      isOpen={isOpen}
      className="min-h-[20rem] min-w-[20rem]"
      size="3xl"
    >
      {confirmedChange == null && (
        <BaseChangeStatusForm
          status={course.status}
          onSubmit={(x) => setConfirmedChange(x)}
          onCancel={onCloseRequest}
        />
      )}
      {confirmedChange == COURSE_STATUS.DRAFT && <DraftCourseChange onSubmit={onConfirmDraft} />}
      {confirmedChange == COURSE_STATUS.PLANNED && (
        <PlannedCourseDates course={course} onCancel={() => setConfirmedChange(null)} onCloseRequest={onCloseRequest} />
      )}
      {confirmedChange == COURSE_STATUS.ARCHIVED && (
        <ArchiveCourseChange onSubmit={onConfirmArchived} onCancel={() => setConfirmedChange(null)} />
      )}
      {confirmedChange == COURSE_STATUS.READY && (
        <ReadyCourseChange onSubmit={onConfirmedReady} onCancel={() => setConfirmedChange(null)} courseId={course.id} />
      )}
      {confirmedChange == COURSE_STATUS.ONGOING && (
        <OngoingCourseChange onSubmit={onConfirmedOngoing} onCancel={() => setConfirmedChange(null)} />
      )}
    </PaziaModal>
  );
};

const BaseChangeStatusForm = ({
  status,
  onSubmit,
  onCancel,
}: {
  status: number;
  onSubmit: (status: number) => void;
  onCancel: () => void;
}) => {
  const methods = useForm({
    resolver: yupResolver(schema),
  });
  const { handleSubmit } = methods;

  const onSubmitHandler = (data: FormValues) => {
    onSubmit(data.newStatus);
    methods.reset();
  };
  return (
    <FormProvider {...methods}>
      <ModalPadding className="flex min-h-96 flex-col" form onSubmit={handleSubmit(onSubmitHandler)}>
        <ModalHeader>Zmień status kursu</ModalHeader>
        <div className="grow">
          <PaziaSelect
            isClearable={false}
            label="Wybierz nowy status dla kursu"
            name="newStatus"
            options={COURSE_STATUS_OPTIONS.filter((x) => x.value !== status)}
            onChange={() => {}}
          />
          <div className="py-4">
            <span>Opis statusów</span>
            <ul className="list-disc pl-6">
              <li>
                <b>Wersja robocza</b> - kurs nie będzie widoczny dla użytkowników.
              </li>
              <li>
                <b>Zaplanowany</b> - kurs będzie widoczny dla użytkowników, mimo niekompletnych danych.
              </li>
              <li>
                <b>Gotowy</b> - kurs będzie widoczny dla użytkowników. Posiada wszystkie wymagane dane.
              </li>
              <li>
                <b>Trwający</b> - ustawiany automatycznie w dniu pierwszego wydarzenia z harmonogramu (można go też
                ustawić ręcznie). Kurs przestaje być widoczny w sklepie (chyba że grupa ma włączone „Pokazuj trwające
                edycje” w ustawieniach sklepu), a zapisy zostają zamknięte (chyba że włączono zapisy po rozpoczęciu
                kursu).
              </li>
              <li>
                <b>Zarchiwizowany</b> - kurs nie będzie widoczny dla użytkowników. Użytkownicy, którzy uczestniczyli w
                kursie, będą mogli go zobaczyć w historii.
              </li>
            </ul>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <PaziaButton btnType="button" title="Anuluj" type="tertiary" onClick={onCancel} />
          <PaziaButton title="Dalej" type="primary" />
        </div>
      </ModalPadding>
    </FormProvider>
  );
};

const DraftCourseChange = ({ onSubmit }: { onSubmit: () => void }) => {
  return (
    <ModalPadding className="flex min-h-96 flex-col">
      <ModalHeader>Zmień status kursu</ModalHeader>
      <div className="grow">
        <div>
          Zmiana statusu na <b>Wersja robocza</b> oznacza, że kurs nie będzie dostępny dla użytkowników.
        </div>
      </div>
      <div className="flex justify-end">
        <PaziaButton title="Zmień status" type="success" onClick={onSubmit} />
      </div>
    </ModalPadding>
  );
};

const ArchiveCourseChange = ({ onSubmit, onCancel }: { onSubmit: () => void; onCancel: () => void }) => {
  return (
    <ModalPadding className="flex min-h-96 flex-col">
      <ModalHeader>Zmień status kursu</ModalHeader>
      <div className="grow">
        <div>
          Zmiana statusu na <b>Zarchiwizowany</b> oznacza, że kurs nie będzie dostępny dla użytkowników.
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <PaziaButton btnType="button" title="Anuluj" type="tertiary" onClick={onCancel} />
        <PaziaButton title="Zmień status" type="success" onClick={onSubmit} />
      </div>
    </ModalPadding>
  );
};

const ReadyCourseChange = ({
  onSubmit,
  onCancel,
  courseId,
}: {
  onSubmit: () => void;
  onCancel: () => void;
  courseId: number;
}) => {
  return (
    <ModalPadding className="flex min-h-96 flex-col">
      <ModalHeader>Zmień status kursu</ModalHeader>

      <CourseStatus courseId={courseId} />
      <div className="pb-2grow mt-6">
        <div>
          Zmiana statusu na <b>Gotowy</b> oznacza, że zapisani kursanci zostaną poinformowani o wszelakich zmianach
          wprowadzanych w kursie.
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <PaziaButton btnType="button" title="Anuluj" type="tertiary" onClick={onCancel} />
        <PaziaButton title="Zmień status" type="success" onClick={onSubmit} />
      </div>
    </ModalPadding>
  );
};

const OngoingCourseChange = ({ onSubmit, onCancel }: { onSubmit: () => void; onCancel: () => void }) => {
  return (
    <ModalPadding className="flex min-h-96 flex-col">
      <ModalHeader>Zmień status kursu</ModalHeader>
      <div className="flex grow flex-col gap-2">
        <span>
          Status kursu zostanie zmieniony na <b>Trwający</b>.
        </span>
        <PaziaBanner type="warning">
          Kurs przestanie być widoczny w sklepie, chyba że grupa ma włączone „Pokazuj trwające edycje” w ustawieniach
          sklepu. Zapisy zostaną zamknięte, chyba że w sekcji Zapisy włączono „Pozwól zapisać się po rozpoczęciu kursu”.
        </PaziaBanner>
      </div>
      <div className="flex justify-end gap-2">
        <PaziaButton btnType="button" title="Anuluj" type="tertiary" onClick={onCancel} />
        <PaziaButton title="Zmień status" type="success" onClick={onSubmit} />
      </div>
    </ModalPadding>
  );
};

interface PlannedCourseDatesProps {
  course: CourseBaseInfoDto;
  onCancel: () => void;
  onCloseRequest: () => void;
}

const PlannedCourseDates = ({ course, onCancel, onCloseRequest }: PlannedCourseDatesProps) => {
  const { showError, showSuccess } = useToaster();
  const onSubmitHandler = async () => {
    try {
      const request = {
        status: COURSE_STATUS.PLANNED,
      };
      await coursesApi.changeStatusToPlanned(course.id, request);
      showSuccess("Status kursu zaktualizowany");
    } catch (error) {
      showError("Nie udało się zmienić statusu kursu");
    } finally {
      onCloseRequest();
    }
  };
  return (
    <ModalPadding className="flex min-h-96 flex-col">
      <ModalHeader>Zmień status kursu</ModalHeader>
      <div className="flex grow flex-col gap-2">
        <span>
          Status kursu zostanie zmieniony na <b>Zaplanowany</b>.
        </span>
        <PaziaBanner type="warning">Kurs będzie widoczny dla użytkowników.</PaziaBanner>
      </div>
      <div className="flex justify-end gap-2">
        <PaziaButton btnType="button" title="Anuluj" type="tertiary" onClick={onCancel} />
        <PaziaButton title="Zmień status" type="success" onClick={() => onSubmitHandler()} />
      </div>
    </ModalPadding>
  );
};
